import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core'
import { darken } from 'polished'
import React, { useMemo } from 'react'
import { Activity } from 'react-feather'
import styled from 'styled-components'
import { NetworkContextName } from '../../constants'
import useENSName from '../../hooks/useENSName'
import { useHasSocks } from '../../hooks/useSocksBalance'
import { useWalletModalToggle } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/reducer'
import { shortenAddress } from '../../utils'
import { ButtonSecondary } from '../Button'
import Loader from '../Loader'
import { RowBetween } from '../Row'
import WalletModal from '../WalletModal'
import iconWallet from 'assets/svg/connect-wallet.svg'

const Web3StatusGeneric = styled(ButtonSecondary)`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.5rem;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;
  display:flex;

  padding: 15px 24px ;
  color: #FFF;
  background-image: linear-gradient(to bottom, #11314a, #0c2539);

  border-radius: 100px;

  transition: all 0.15s ease-in-out;
  
  &:after {
      content: '';
      position: absolute;
      left: 22px;
      bottom: 0;
      right: 22px;
      display: block;
      height: 1.25rem;
      background-color: #5cb6c8;
      background-position: 50%;
      opacity: 0;

      filter: blur(22px);

      transition: all 0.15s ease-in-out;

    }


  }

  :focus {
    outline: none;
  }
  :focus,
  :hover, 
  :active {
    border: solid 1px #5cb6c8;

    &:after {
       opacity: 60%;
    }


  }
  & > img {
    width: 17px;
    height: 13px;
    margin: 1.5px 7.6px 1.3px 0;
    object-fit: contain;
  }
`
const Web3StatusError = styled(Web3StatusGeneric)`
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  :hover,
  :focus,
  :active {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};

    border: solid 1px ${({ theme }) => theme.red1};

    &:after {
      background-color: ${({ theme }) => darken(0.1, theme.red1)};
      opacity: 30%;
    }
  }
`
const Text = styled.p`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content;

  margin: 0;

  font-size: 13px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.93px;
  text-align: center;
  color: #fff;
`
const NetworkIcon = styled(Activity)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

const SOCK = (
  <span role="img" aria-label="has socks emoji" style={{ marginTop: -4, marginBottom: -4 }}>
    🧦
  </span>
)

function Web3StatusInner() {
  const { account, error } = useWeb3React()
  const { ENSName } = useENSName(account ?? undefined)
  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter(tx => !tx.receipt).map(tx => tx.hash)

  const hasPendingTransactions = !!pending.length
  const hasSocks = useHasSocks()
  const toggleWalletModal = useWalletModalToggle()

  if (account) {
    return (
      <Web3StatusGeneric id="web3-status-connected" onClick={toggleWalletModal} pending={hasPendingTransactions}>
        <RowBetween>
          <img src={iconWallet} alt="connect wallet" />
          {hasPendingTransactions ? (
            <>
              <Text>{pending?.length} Pending</Text> <Loader stroke="white" />
            </>
          ) : (
            <>
              {hasSocks ? SOCK : null}
              <Text>{ENSName || shortenAddress(account)}</Text>
            </>
          )}
          {/* {!hasPendingTransactions && connector && <StatusIcon connector={connector} />} */}
        </RowBetween>
      </Web3StatusGeneric>
    )
  } else if (error) {
    return (
      <Web3StatusError onClick={toggleWalletModal}>
        <NetworkIcon />
        <Text>{error instanceof UnsupportedChainIdError ? 'Wrong Network' : 'Error'}</Text>
      </Web3StatusError>
    )
  } else {
    return (
      <Web3StatusGeneric id="connect-wallet" onClick={toggleWalletModal} faded={!account}>
        <RowBetween>
          <img src={iconWallet} alt="connect wallet" />

          <Text> Connect </Text>
        </RowBetween>
      </Web3StatusGeneric>
    )
  }
}

export default function Web3Status2() {
  const { active, account } = useWeb3React()
  const contextNetwork = useWeb3React(NetworkContextName)
  const { ENSName } = useENSName(account ?? undefined)
  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter(tx => !tx.receipt).map(tx => tx.hash)
  const confirmed = sortedRecentTransactions.filter(tx => tx.receipt).map(tx => tx.hash)

  if (!contextNetwork.active && !active) {
    return null
  }

  return (
    <>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
